import { useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useNetworkStore } from '@/stores/network-store';
import { runCloudSync } from '@/services/cloud-sync.service';

/**
 * Motor de sincronización en tiempo real vía WebSockets (Socket.io).
 *
 * En lugar de hacer polling cada 60s, este hook mantiene una conexión activa por WebSocket.
 * - Al conectar/reconectar, vacía la cola de transacciones locales pendientes (`synced = 0`).
 * - Escucha avisos `data_updated` del servidor para actualizar catálogos cuando el administrador cambia algo en la Nube.
 */
export function useSocketSync() {
  const mode = useNetworkStore((state) => state.mode);
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (mode !== 'local' && mode !== 'local_master') {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      return;
    }

    const companyId = localStorage.getItem('COMPANY_ID');
    const branchId = localStorage.getItem('BRANCH_ID');
    const registerName = localStorage.getItem('REGISTER_NAME') || 'Caja';

    if (!companyId || !branchId) return;

    // Determinar la URL del servidor WebSocket (Servidor central)
    const isElectron = navigator.userAgent.toLowerCase().includes('electron');
    const cloudApiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';

    console.log('[SocketSync] Conectando canal WebSocket:', cloudApiUrl);

    const socket = io(`${cloudApiUrl}/sync-ws`, {
      transports: ['websocket', 'polling'],
      autoConnect: true,
      reconnection: true,
      reconnectionDelay: 3000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('[SocketSync] ✅ Conectado por WebSocket con ID:', socket.id);
      
      // Unirse a la sala privada de la empresa/sucursal
      socket.emit('join_room', { companyId, branchId, registerName });

      // Disparar sincronización inmediata de la cola acumulada durante la desconexión
      runCloudSync({
        onStatus: (msg) => console.log(`[SocketSync:OnConnect] ${msg}`),
      }).catch((err) => console.warn('[SocketSync] Error vaciando cola offline:', err));
    });

    socket.on('disconnect', (reason) => {
      console.warn('[SocketSync] ⚠️ WebSocket desconectado:', reason);
    });

    socket.on('joined_room_ack', (data) => {
      console.log('[SocketSync] Confirmada unión a la sala:', data);
    });

    socket.on('data_updated', (data) => {
      console.log('[SocketSync] 🔔 Cambio en la Nube recibido por WebSocket:', data);
      runCloudSync({
        onStatus: (msg) => console.log(`[SocketSync:DataUpdated] ${msg}`),
      }).catch((err) => console.warn('[SocketSync] Error actualizando datos:', err));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [mode]);

  return {
    emitPush: () => {
      // Forzar ejecucion de la rutina de sync inmediatamente
      runCloudSync().catch(() => null);
    },
  };
}
